/**
 * SubNav — horizontal pill-tab strip rendered at the top of vendor-scoped
 * pages. Lets the user move between sibling pages without using the sidebar.
 *
 * Each item is either:
 *   - { to, label } — a real React Router link with active highlighting
 *   - { label, dim: true } — a "coming soon" item that's visible but not clickable
 *
 * Active items pulse once on mount so the user notices the strip exists. The
 * pulse plays a single time per page navigation (not on every parent re-render).
 */
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/cn";

export interface SubNavItem {
  /** Visible label. */
  label: string;
  /** Real route — omit (and set `dim: true`) for placeholders. */
  to?: string;
  /** Render greyed out and unclickable (used for not-yet-built pages). */
  dim?: boolean;
}

export interface SubNavProps {
  items: SubNavItem[];
  className?: string;
}

export function SubNav({ items, className }: SubNavProps) {
  return (
    <nav
      aria-label="Vendor sub-navigation"
      className={cn(
        "inline-flex w-fit max-w-full items-center gap-1 overflow-x-auto rounded-xl border border-line bg-surface p-1",
        className,
      )}
    >
      {items.map((item) => {
        // Dim placeholder — not a link.
        if (item.dim || !item.to) {
          return (
            <span
              key={item.label}
              className="cursor-not-allowed whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-semibold text-fg-mute opacity-60"
              title="Coming soon"
            >
              {item.label}
            </span>
          );
        }

        return (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "relative whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors",
                "text-fg-mute hover:text-fg",
                isActive &&
                  "bg-aurora-soft text-violet shadow-[inset_0_0_0_1px_rgba(125,108,255,0.25)]",
                isActive && "animate-[subnav-pulse_900ms_ease-out_1]",
              )
            }
          >
            {item.label}
          </NavLink>
        );
      })}

      {/* Inline keyframes — single-shot pulse on the active tab so the
          existence of the strip is hard to miss when first landing on a page. */}
      <style>{`
        @keyframes subnav-pulse {
          0%   { box-shadow: inset 0 0 0 1px rgba(125,108,255,0.25), 0 0 0 0 rgba(125,108,255,0.5); }
          50%  { box-shadow: inset 0 0 0 1px rgba(125,108,255,0.25), 0 0 0 6px rgba(125,108,255,0); }
          100% { box-shadow: inset 0 0 0 1px rgba(125,108,255,0.25), 0 0 0 0 rgba(125,108,255,0); }
        }
      `}</style>
    </nav>
  );
}
